import { TableWithStoredSettingsAndLayoutSettings } from '@Components/Tables/TableWithStoredSettingsAndLayoutSettings';
import { Permission } from '@Constants/Permission.g';
import { useHasPermission } from '@Contexts/appContext';
import { useIsPortalSession } from '@Contexts/session';
import { usePortalContainerTypeLabels } from '@Hooks/Stammdaten/usePortalLabels';
import { emptyPage } from '@Hooks/useListApi';
import { useRemoteCacheInvalidation } from '@Hooks/useRemoteCacheInvalidation';
import { useAuftragRepository } from '@Hooks/useRepository';
import { IHasTableModes } from '@Hooks/useTableModes';
import { type ApiContainerStockGroup, type EnumContainerActionRequest, EnumFrontendLayoutSetting } from '@Model';
import { ContainerStockRepository } from '@Repositories/Auftragsbearbeitung';
import {
    Badge,
    DateInputBase,
    type ExpandedState,
    FloatingToolbar,
    FlexColumn,
    type ListInfo,
    type ListRequest,
    type ToolbarOptions,
    unwrapOr,
    useTableRefetch,
} from '@synqo/frontend-component-library';
import { useCallback, useMemo, useState } from 'react';
import {
    containerStockColumns,
    ContainerStockCorrectionModal,
    type ContainerStockGroup,
    containerStockLineColumns,
    ContainerStockLines,
    type ContainerStockRow,
    FollowUpButtons,
    hasNoLinesToExpand,
    portalContainerStockColumns,
    toggledExpansion,
    toStockGroups,
    useContainerSelection,
    withStandingForTranslated,
} from '../Editing';
import { ContainerStockTableModes } from './containerStockTableModes';
import { useContainerPlanningOnNewDraft } from './useContainerPlanningOnNewDraft';
import { useContainerStockTableModeToolbar } from './useContainerStockTableModeToolbar';

/** The container stock and the container actions that are planned for it, one table mode each. */
export function FrameListStandingContainerStock(tableModeProps: IHasTableModes<ContainerStockTableModes>) {
    const isPortalSession = useIsPortalSession();
    const hasPermission = useHasPermission();
    const { canPlan: canSelectForFollowUp, isPlanning: isBusy, plan } = useContainerPlanningOnNewDraft();
    const { selected, allowedFollowUps, groupSelector, lineSelectorOf, clear, track } =
        useContainerSelection<ContainerStockGroup>(isBusy);

    const canCorrect = !isPortalSession && hasPermission(Permission.AuftragsbearbeitungContainerStockUpdate);
    const canAdd = !isPortalSession && hasPermission(Permission.AuftragsbearbeitungContainerStockCreate);
    const [correcting, setCorrecting] = useState<{ row: ContainerStockRow | undefined } | undefined>(undefined);
    const [expanded, setExpanded] = useState<ExpandedState>({});
    const [validAt, setValidAt] = useState('');

    const containerStock = useAuftragRepository(ContainerStockRepository);
    const { ensure: ensureTypeNames, labelOf: containerTypeOf } = usePortalContainerTypeLabels();

    const loadGroups = useCallback(
        async (request: ListRequest): Promise<ListInfo<ContainerStockGroup>> => {
            const loaded = unwrapOr(
                await containerStock.getStockGroups(
                    withStandingForTranslated(request, new Date()),
                    validAt === '' ? undefined : new Date(validAt).toISOString(),
                ),
                emptyPage<ApiContainerStockGroup>(),
            );
            ensureTypeNames(loaded.items.map((group) => group.container_type));

            const groups = toStockGroups(loaded.items);
            track(groups, request.page_no);

            return { ...loaded, items: groups };
        },
        [containerStock, ensureTypeNames, track, validAt],
    );
    const { refetchRef, refetchTable } = useTableRefetch();
    const reload = useCallback(() => refetchRef.current(), [refetchRef]);
    useRemoteCacheInvalidation('orders', refetchTable);

    const startFollowUp = useCallback(
        async (kind: EnumContainerActionRequest) => {
            const opened = await plan(kind, { standingTimeIds: [...selected] });
            if (opened) {
                clear();
            }
        },
        [selected, clear, plan],
    );

    const correct = useCallback((row: ContainerStockRow) => {
        setCorrecting({ row });
        return true;
    }, []);

    const selectGroup = useCallback(
        (group: ContainerStockGroup) => {
            if (!hasNoLinesToExpand(group)) {
                setExpanded((current) => toggledExpansion(current, group.id));
            } else if (canCorrect) {
                correct(group.lines[0]);
            }

            return true;
        },
        [canCorrect, correct],
    );

    const stockToolbarElements = useContainerStockTableModeToolbar(tableModeProps);
    const toolbarElements = useCallback(
        (elements: ToolbarOptions[]) => [
            ...stockToolbarElements(elements),
            <FlexColumn key="validAt" width="13rem" shrink={false} align="stretch">
                <DateInputBase
                    label="Stichtag"
                    name="stock_valid_at"
                    type="datetime"
                    ariaLabel="Stichtag"
                    placeholder="jetzt"
                    clearable
                    value={validAt}
                    onChange={(value) => {
                        setValidAt(value);
                        return true;
                    }}
                />
            </FlexColumn>,
        ],
        [validAt, stockToolbarElements, setValidAt],
    );

    const columns = useMemo(
        () => (isPortalSession ? portalContainerStockColumns(containerTypeOf) : containerStockColumns),
        [isPortalSession, containerTypeOf],
    );

    return (
        <>
            <TableWithStoredSettingsAndLayoutSettings<ContainerStockGroup>
                layoutSettingsContext={EnumFrontendLayoutSetting.ContainerStockTableLayout}
                name="ContainerStockList"
                queryKey={['ContainerStockList', validAt]}
                toolbarElements={toolbarElements}
                columns={columns}
                loader={loadGroups}
                refetchRef={refetchRef}
                objectSelector={canSelectForFollowUp ? groupSelector : undefined}
                renderSubComponent={(group) => (
                    <ContainerStockLines
                        group={group}
                        columns={containerStockLineColumns}
                        onSelect={canCorrect ? correct : undefined}
                        objectSelector={canSelectForFollowUp ? lineSelectorOf(group) : undefined}
                    />
                )}
                disableExpanding={hasNoLinesToExpand}
                forceExpand={{ expanded, setExpanded }}
                onSelect={selectGroup}
                canCreate={canAdd}
                onAdd={() => {
                    setCorrecting({ row: undefined });
                    return true;
                }}
                addLabel="Container hinzufügen"
                fullSize
            />
            {canSelectForFollowUp && selected.size > 0 && (
                <FloatingToolbar>
                    <Badge variant="success" type="input">
                        {selected.size}
                    </Badge>
                    <FollowUpButtons
                        allowedFollowUps={allowedFollowUps}
                        locked={isBusy}
                        onFollowUp={(kind) => void startFollowUp(kind)}
                    />
                </FloatingToolbar>
            )}
            {correcting && (
                <ContainerStockCorrectionModal
                    row={correcting.row}
                    onClose={() => setCorrecting(undefined)}
                    onSaved={reload}
                />
            )}
        </>
    );
}
